import { useState, type MouseEvent, type ReactNode } from 'react';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import {
  MoreVert,
  Person,
  PersonRemove,
  PersonOff,
  PhoneCallback,
  PhoneDisabledOutlined,
  VisibilityOutlined,
  VisibilityOffOutlined,
  RemoveCircleOutline,
} from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import {
  contributorRemoteSpeakerActionLabel,
  contributorVisibilityActionLabel,
  type ContributorItemLabelContext,
} from '@/components/events/detail/tabs/common/contributors/contributor-item-action-labels';

export type ContributorItemOverflowMenuProps = {
  /** Which i18n variant to use for remote speaker / visibility menu labels. */
  labelContext?: ContributorItemLabelContext;
  showAutoCreate?: boolean;
  autoCreateEnabled?: boolean;
  onToggleAutoCreate?: () => void;
  showRemoteSpeaker?: boolean;
  remoteSpeakerEnabled?: boolean;
  onToggleRemoteSpeaker?: () => void;
  showVisibility?: boolean;
  visibilityEnabled?: boolean;
  onToggleVisibility?: () => void;
  showDelete?: boolean;
  /** Library / slot removal → RemoveCircleOutline; session or flow removal → PersonRemove. */
  deleteAction?: 'delete' | 'removeFromSlot';
  /** What is being removed when deleteAction is delete. */
  deleteTarget?: 'session' | 'flow';
  deleteDisabled?: boolean;
  onDelete?: () => void;
};

export function ContributorItemOverflowMenu({
  labelContext = 'session',
  showAutoCreate = false,
  autoCreateEnabled = true,
  onToggleAutoCreate,
  showRemoteSpeaker = false,
  remoteSpeakerEnabled = false,
  onToggleRemoteSpeaker,
  showVisibility = false,
  visibilityEnabled = true,
  onToggleVisibility,
  showDelete = false,
  deleteAction = 'delete',
  deleteTarget = 'session',
  deleteDisabled = false,
  onDelete,
}: ContributorItemOverflowMenuProps) {
  const t = useTranslations();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const menuKey = (suffix: string) => `event.detail.contributors.overflowMenu.${suffix}` as TranslationKey;

  const hasMenuItems =
    (showAutoCreate && onToggleAutoCreate) ||
    (showRemoteSpeaker && onToggleRemoteSpeaker) ||
    (showVisibility && onToggleVisibility) ||
    (showDelete && onDelete);

  if (!hasMenuItems) {
    return null;
  }

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const runAction = (action?: () => void) => (event: MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    handleClose();
    action?.();
  };

  const remoteSpeakerPrimary = contributorRemoteSpeakerActionLabel(t, remoteSpeakerEnabled);
  const visibilityPrimary = contributorVisibilityActionLabel(t, labelContext, visibilityEnabled);

  const autoCreatePrimary = autoCreateEnabled ? t(menuKey('disableAutoAssign')) : t(menuKey('enableAutoAssign'));

  const deletePrimary =
    deleteAction === 'removeFromSlot'
      ? t(menuKey('removeFromSlot'))
      : deleteTarget === 'flow'
        ? t(menuKey('removeFromFlow'))
        : t(menuKey('removeFromSession'));

  const deleteIcon: ReactNode =
    deleteAction === 'removeFromSlot' ? (
      <RemoveCircleOutline fontSize="small" />
    ) : (
      <PersonRemove fontSize="small" color="error" />
    );

  return (
    <>
      <Tooltip title={t('common.actions' as TranslationKey)}>
        <IconButton
          size="small"
          aria-label={t('common.actions' as TranslationKey)}
          onClick={handleOpen}
          data-testid="contributor-item-overflow-menu"
          sx={{
            'padding': '2px',
            '& > svg': {
              fontSize: '1.25rem',
              width: '1.25rem',
              height: '1.25rem',
            },
          }}
        >
          <MoreVert />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {showAutoCreate && onToggleAutoCreate ? (
          <MenuItem onClick={runAction(onToggleAutoCreate)} data-testid="contributor-overflow-auto-create">
            <ListItemIcon>
              {autoCreateEnabled ? <PersonOff fontSize="small" /> : <Person fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary={autoCreatePrimary} />
          </MenuItem>
        ) : null}
        {showRemoteSpeaker && onToggleRemoteSpeaker ? (
          <MenuItem onClick={runAction(onToggleRemoteSpeaker)} data-testid="contributor-overflow-remote-speaker">
            <ListItemIcon>
              {remoteSpeakerEnabled ? (
                <PhoneDisabledOutlined fontSize="small" />
              ) : (
                <PhoneCallback fontSize="small" sx={{ transform: 'scaleX(-1)' }} />
              )}
            </ListItemIcon>
            <ListItemText primary={remoteSpeakerPrimary} />
          </MenuItem>
        ) : null}
        {showVisibility && onToggleVisibility ? (
          <MenuItem onClick={runAction(onToggleVisibility)} data-testid="contributor-overflow-visibility">
            <ListItemIcon>
              {visibilityEnabled ? <VisibilityOffOutlined fontSize="small" /> : <VisibilityOutlined fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary={visibilityPrimary} />
          </MenuItem>
        ) : null}
        {showDelete && onDelete ? (
          <MenuItem
            onClick={runAction(onDelete)}
            disabled={deleteDisabled}
            data-testid={
              deleteAction === 'removeFromSlot'
                ? 'contributor-overflow-remove-from-slot'
                : 'contributor-overflow-delete'
            }
          >
            <ListItemIcon>{deleteIcon}</ListItemIcon>
            <ListItemText primary={deletePrimary} />
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
}
